// เข้าสู่ระบบด้วย PIN 6 หลัก (เหมือนระบบเดิม) — แป้นตัวเลขใหญ่ กดง่ายบนมือถือ
// ถ้าเซิร์ฟเวอร์เปิด Cloudflare Turnstile (/auth/config มี site key) → แสดงวิดเจ็ตยืนยันว่าไม่ใช่บอท แล้วส่ง token ไปพร้อม PIN
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Delete, Loader2, Wrench } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cx } from '../components/ui';

const LEN = 6;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string | undefined;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loader: Promise<TurnstileApi> | null = null;
/** โหลดสคริปต์ Turnstile ครั้งเดียวต่อหน้า */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loader ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = TURNSTILE_SRC;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')));
    s.onerror = () => {
      loader = null;
      s.remove();
      reject(new Error('turnstile'));
    };
    document.head.appendChild(s);
  });
  return loader;
}

/** วิดเจ็ต Turnstile แบบ render เอง — token ใช้ได้ครั้งเดียว ต้อง reset หลังส่งทุกครั้ง */
function useTurnstile(siteKey: string | null) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !ref.current) return;
        widgetId.current =
          ts.render(ref.current, {
            sitekey: siteKey,
            action: 'login',
            theme: 'light',
            size: 'flexible',
            language: 'th',
            callback: (t: string) => {
              setToken(t);
              setFailed(false);
            },
            'expired-callback': () => setToken(null),
            'error-callback': () => {
              setToken(null);
              setFailed(true);
            },
          }) ?? null;
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetId.current) window.turnstile?.reset(widgetId.current);
  }, []);

  return { ref, token, failed, reset };
}

export function Login() {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<{ message: string; locked: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<{ turnstileSiteKey: string | null }>('/auth/config'),
    staleTime: Infinity,
    retry: 1,
  });
  const siteKey = cfg.data?.turnstileSiteKey ?? null;
  const ts = useTurnstile(siteKey);

  const submit = async (value: string, token: string | null) => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/login', token ? { pin: value, turnstileToken: token } : { pin: value });
      qc.clear();
      navigate('/', { replace: true });
    } catch (e) {
      setError({ message: e instanceof ApiError ? e.message : 'เข้าสู่ระบบไม่สำเร็จ', locked: e instanceof ApiError && e.status === 429 });
      setPin('');
    } finally {
      if (siteKey) ts.reset();
      setBusy(false);
    }
  };

  // ครบ 6 หลัก → ส่ง (ถ้าเปิด Turnstile ต้องรอให้วิดเจ็ตยืนยันเสร็จก่อน)
  const waiting = pin.length === LEN && !busy && (cfg.isPending || (!!siteKey && !ts.token));
  useEffect(() => {
    if (pin.length !== LEN || busy || waiting) return;
    void submit(pin, siteKey ? ts.token : null);
    // ส่งเฉพาะตอน pin/busy/waiting เปลี่ยน (token ใหม่มาถึง = waiting เปลี่ยนเป็น false)
  }, [pin, busy, waiting]);

  const press = (d: string) => {
    if (busy) return;
    setError(null);
    setPin((p) => (p + d).slice(0, LEN));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-gradient-to-b from-brand-soft to-paper px-4 py-10">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="grid size-20 place-items-center rounded-3xl bg-brand text-white shadow-lg">
          <Wrench className="size-11" />
        </div>
        <h1 className="text-4xl font-bold">SETCOM งานซ่อม</h1>
        <p className="text-xl text-muted">ใส่ PIN 6 หลักเพื่อเข้าใช้งาน</p>
      </div>

      <div className="flex gap-3" aria-label={`ใส่แล้ว ${pin.length} หลัก`}>
        {Array.from({ length: LEN }, (_, i) => (
          <span key={i} className={cx('size-5 rounded-full border-2 transition', i < pin.length ? 'scale-110 border-brand bg-brand' : 'border-muted/40 bg-white')} />
        ))}
      </div>

      <div className="flex min-h-8 w-full max-w-xs items-center justify-center text-center text-lg font-semibold" role="alert">
        {busy ? (
          <Loader2 className="size-7 animate-spin text-brand" />
        ) : error?.locked ? (
          <p className="flex items-start gap-2 rounded-2xl bg-st-amber-soft px-4 py-3 text-left text-ink">
            <Clock className="mt-0.5 size-6 shrink-0 text-st-amber" />
            {error.message}
          </p>
        ) : error ? (
          <span className="text-st-rose">{error.message}</span>
        ) : waiting ? (
          <span className="flex items-center gap-2 text-muted">
            <Loader2 className="size-5 animate-spin" /> รอยืนยันว่าไม่ใช่บอทสักครู่…
          </span>
        ) : null}
      </div>

      <div className="grid w-full max-w-xs grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <Key key={d} onClick={() => press(d)}>
            {d}
          </Key>
        ))}
        <span />
        <Key onClick={() => press('0')}>0</Key>
        <Key onClick={() => setPin((p) => p.slice(0, -1))} label="ลบ">
          <Delete className="size-8" />
        </Key>
      </div>

      {siteKey && (
        <div className="w-full max-w-xs">
          <div ref={ts.ref} />
          {ts.failed && <p className="mt-2 text-center text-base text-st-rose">โหลดตัวยืนยันว่าไม่ใช่บอทไม่สำเร็จ กรุณารีเฟรชหน้าแล้วลองใหม่</p>}
        </div>
      )}
    </div>
  );
}

function Key({ children, onClick, label }: { children: React.ReactNode; onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid aspect-square place-items-center rounded-3xl bg-white font-display text-4xl font-bold text-ink shadow-sm ring-1 ring-line transition hover:bg-brand-soft active:scale-95 active:bg-brand active:text-white"
    >
      {children}
    </button>
  );
}
